import AcUnitIcon from '@mui/icons-material/AcUnit';
import ViewComfy from '@mui/icons-material/ViewComfy';
import ViewList from '@mui/icons-material/ViewList';
import { Grid, Link as MuiLink, Paper, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import { useErrorContext } from '@qpr/client-library';
import { ErrorObject } from '@qpr/client-library/dist/esm/ErrorContext';
import {
  BreadCrumb,
  Card,
  CenterItems,
  Content,
  Flex,
  FormControl,
  IconButton,
  IndicatorCircle,
  IndicatorTitle,
  KPITableCell,
  KPITableContainer,
  KPITableHeader,
  KPITableHeaderCell,
  KPITableRow,
  KPIToolBar,
  KPIToolBarMenu,
  KPIToolBarMenuItem,
  LinearProgress,
  Select,
  Separator,
  ToggleButton,
  ToggleButtonGroup
} from '@qpr/components';
import axios, { AxiosError, AxiosResponse } from 'axios';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { Link as RouterLink, useLocation, useRouteMatch } from 'react-router-dom';
import { Range as RangeItem, useRanges } from '../api/useRanges';
import AppBarContext from '../components/BusinessPortalAppBarContext';
import Donut from '../modules/DonutChart/DonutChart';
import { isMongoId } from '../utils/url-utils';

interface Data {
  id: string;
  name: string;
  typename: string;
  parentId: string;
  description: string;
  children: Data[];
  rangecolor_actual: string;
  rangevalue_actual: string;
  rangename_actual: string;
  value_actual: string;
  value_target: string;
  prettyvalue_actual: string;
  prettyrangevalue_actual: string;
}
interface KpiPageParams {
  tenantSymbol: string;
  meaId: string;
  viewdefinitionId?: string;
}

interface Aim {
  name: string;
  id: string;
  kpis: Array<Data>;
  valuesettingid: any;
}

const fetchSubMeasures = async (parentIds: Array<string>, periodName: string, series: any = {}) => {
  if (parentIds.length === 0) return [];

  let string = `name, typename, description, measure.parent.name(as="parentname"), valuesettingid`;

  if (!('actual' in series)) series.actual = 'ACT';
  if (!('target' in series)) series.target = 'TAR';

  string += `, prettyvalue(series="${series.actual}", period=${periodName}, as="prettyvalue_actual"), value(series="${series.actual}", period=${periodName}, as="value_actual"), value(series="${series.target}", period=${periodName}, as="value_target"), rangecolor(series="${series.actual}", period=${periodName}, as="rangecolor_actual"), prettyrangevalue(series="${series.actual}", period=${periodName}, as="prettyrangevalue_actual"), rangename(series="${series.actual}", period=${periodName}, as="rangename_actual"), rangevalue(series="${series.actual}", period=${periodName}, as="rangevalue_actual")`;
  const response = await axios.post('measurehierarchy', {
    parentId: parentIds.join(),
    recursive: true,
    recursioncount: 2,
    attributes: string,
    sortby: 'level,order,name',
    informationitems: true
  });
  return response.data;
};

const useAimsKpis = (meaId: string, periodName: string, viewdefinitionId: string | null) =>
  useQuery<AxiosResponse<Data[]>, AxiosError<ErrorObject>>(
    `kpiaim-${meaId}-${periodName}-${viewdefinitionId}`,
    async () => {
      let seriesToQuery: string[] = [];

      if (viewdefinitionId && isMongoId(viewdefinitionId)) {
        const viewResponse = await axios.get(`/viewdefinitions/${viewdefinitionId}`);
        if (viewResponse.data.data) {
          seriesToQuery = viewResponse.data.data.series || [];
        }
      }
      const parentMeasures = await fetchSubMeasures([meaId], periodName, seriesToQuery);
      const parentIds = parentMeasures.data[0].children.map((parent: any) => parent.id);
      const subMeasures = await fetchSubMeasures(parentIds, periodName, seriesToQuery);

      const tmp: any = [];
      parentMeasures.data[0].children.forEach((child: any) => {
        tmp.push({
          grandParent: parentMeasures.data[0].parentname,
          grandParentId: parentMeasures.data[0].parentid,
          kpis: [],
          ...child
        });
      });

      // attach kpis to aims
      tmp.forEach((aim: any) => {
        subMeasures.data.forEach((relatedChild: any) => {
          if (aim.id === relatedChild.id) aim.kpis = relatedChild.children;
        });
      });
      return tmp;
    },
    {
      // The query will not execute until the selectedPeriod exists
      enabled: !!periodName && !!meaId,
      staleTime: 60 * 1000
    }
  );

const KPIAimView = () => {
  const { params } = useRouteMatch<KpiPageParams>();
  const gotRanges = useRanges(params.meaId, true, 2);

  const { search } = useLocation();
  const [aims, setAims] = useState<Aim[]>([]);
  const [selectedAim, setSelectedAim] = useState(0);
  const [ranges, setRanges] = useState<RangeItem[]>([]);
  const [viewType, setViewType] = useState<'list' | 'grid'>('list');
  const { setError } = useErrorContext();
  const { getPeriodName, setAppBar, setPeriodSelector } = useContext(AppBarContext);
  const queryString = new URLSearchParams(search);
  const viewDefinitionId = queryString.get('viewdefinitionId');
  const [breadcrumbs, setBreadcrumbs] = useState<{ title: string; meaId: string }[]>([]);

  const getUniqueRanges = (selected: number) => {
    const rangesOfSelected = gotRanges.data?.data.filter((range: any) =>
      aims[selected]?.kpis.map((kpi: any) => kpi.valuesettingid).includes(range.valuesettings)
    );

    const noRange = {
      name: '',
      maxseriesid: '',
      maxbelongstorange: '',
      minseriesid: '',
      minbelongstorange: '',
      rangevalue: '',
      color: '000000'
    };

    const uniqueRangeNames: any[] = [];

    if (aims[selected]?.kpis.some(kpi => !kpi.rangename_actual || kpi.rangename_actual === ''))
      uniqueRangeNames.push(noRange);

    rangesOfSelected?.forEach((item: any) => {
      if (!item.name) item.name = '';
      const i = uniqueRangeNames.findIndex(x => x.name === item.name);
      if (i <= -1) {
        uniqueRangeNames.push(item);
      }
    });

    return uniqueRangeNames;
  };

  const switchToViewLinksIfChildren = false;
  useEffect(() => {
    setAppBar(true);
    setPeriodSelector(true);
  }, []);

  useEffect(() => {
    if (gotRanges.data && aims[0]) {
      setRanges(getUniqueRanges(selectedAim));
    } else setRanges([]);
  }, [gotRanges.data, aims]);

  const trimBreadcrumbs = (meaId: string) => {
    const idx = breadcrumbs.findIndex(bc => bc.meaId === meaId);
    if (idx >= 0) setBreadcrumbs(breadcrumbs.slice(0, idx + 1));
  };

  const { isLoading, error, data } = useAimsKpis(params.meaId, getPeriodName(), viewDefinitionId);

  if (Array.isArray(data) && data?.length > 0 && data !== aims) {
    setAims(data as Aim[]);
    if (!breadcrumbs.length || breadcrumbs[breadcrumbs.length - 1].meaId !== params.meaId)
      setBreadcrumbs(
        breadcrumbs.concat(
          data[0].grandParent
            ? [
                { title: data[0].grandParent, meaId: data[0].grandParentId },
                { title: data[0].parentname, meaId: params.meaId }
              ]
            : [{ title: data[0].parentname, meaId: params.meaId }]
        )
      );
  }

  const filteredKpis = aims[selectedAim]?.kpis.filter(kpi => {
    if (ranges.length === 0 || !ranges.some(item => item.disabled)) return true;
    return ranges.find(
      r =>
        !r.disabled && (r.name === kpi.rangename_actual || (!kpi.rangename_actual && r.name === ''))
    );
  });

  const GridView = () => (
    <Grid container spacing={2} data-testid="card-container">
      {filteredKpis?.map(kpi => (
        <Grid item xs={3} key={kpi.id} data-testid="kpi-card">
          <Card>
            <div style={{ width: '100%' }}>
              <CenterItems>
                <Donut
                  data-testid="donut"
                  pros={
                    (parseFloat(kpi.value_actual) / parseFloat(kpi.value_target)) * 100 < 100
                      ? (parseFloat(kpi.value_actual) / parseFloat(kpi.value_target)) * 100
                      : 100
                  }
                  color={`#${kpi.rangecolor_actual}`}
                  title={kpi.prettyvalue_actual}
                />
              </CenterItems>

              <CenterItems>
                <Typography data-testid="kpi-name" variant="subtitle1">
                  {kpi.name}
                </Typography>
              </CenterItems>
            </div>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const TableView = () => (
    <KPITableContainer data-testid="table-container">
      <KPITableHeader>
        <KPITableHeaderCell data-testid="table-header-cell" style={{ width: '20%' }}>
          KPI
        </KPITableHeaderCell>
        <KPITableHeaderCell data-testid="table-header-cell" style={{ width: '20%' }}>
          Progress
        </KPITableHeaderCell>
        <KPITableHeaderCell data-testid="table-header-cell" style={{ width: '10%' }}>
          Value
        </KPITableHeaderCell>
        <KPITableHeaderCell data-testid="table-header-cell">Index</KPITableHeaderCell>
        <KPITableHeaderCell data-testid="table-header-cell">Description</KPITableHeaderCell>
        <KPITableHeaderCell data-testid="table-header-cell" variant-center>
          Performance
        </KPITableHeaderCell>
      </KPITableHeader>

      {filteredKpis?.map(kpi => (
        <KPITableRow key={kpi.id} data-testid="table-row">
          <KPITableCell data-testid="table-cell">
            {kpi.children.length > 0 && switchToViewLinksIfChildren ? (
              <MuiLink
                component={RouterLink}
                to={`/${params.tenantSymbol}/kpiaim/${kpi.id}${search}`}
              >
                {kpi.name}
              </MuiLink>
            ) : (
              <Typography variant="body2">{kpi.name}</Typography>
            )}
          </KPITableCell>
          <KPITableCell data-testid="table-cell">
            <LinearProgress
              variant="determinate"
              value={
                (parseFloat(kpi.value_actual) / parseFloat(kpi.value_target)) * 100 < 100
                  ? (parseFloat(kpi.value_actual) / parseFloat(kpi.value_target)) * 100
                  : 100
              }
            />
          </KPITableCell>
          <KPITableCell data-testid="table-cell">{kpi.prettyvalue_actual}</KPITableCell>
          <KPITableCell data-testid="table-cell">{kpi.prettyrangevalue_actual}</KPITableCell>
          <KPITableCell data-testid="table-cell">{kpi.description}</KPITableCell>
          <KPITableCell data-testid="table-cell" variant-center="true">
            <IndicatorCircle color={`#${kpi.rangecolor_actual}`} size={18} />
          </KPITableCell>
        </KPITableRow>
      ))}
    </KPITableContainer>
  );

  if (error && error.response) setError(error.response.data);

  const getLoadingIndicator = (): JSX.Element => (
    <div data-testid="kpiaim-page-loading" style={{ textAlign: 'center', marginTop: '32px' }}>
      <CircularProgress />
    </div>
  );

  const getPage = (): JSX.Element => (
    <Grid container>
      <Grid item xs={12}>
        <Paper style={{ minHeight: '500px' }}>
          <Content data-testid="kpiaim-page">
            <BreadCrumb>
              {breadcrumbs.map(bc => (
                <Fragment key={bc.meaId}>
                  <MuiLink
                    component={RouterLink}
                    to={`/${params.tenantSymbol}/kpiaim/${bc.meaId}${search}`}
                    onClick={() => trimBreadcrumbs(bc.meaId)}
                    data-testid="breadcrumb-instance"
                  >
                    {bc.title}
                  </MuiLink>
                  <Separator icon="NavigateNext" />
                </Fragment>
              ))}
            </BreadCrumb>

            <div style={{ height: '1em' }} />
            {!!aims.length && (
              <FormControl variant-inline="true">
                <Select
                  MenuProps={{ variant: 'menu' }}
                  data-testid="sibling-selectElement"
                  value={aims[selectedAim]?.name}
                  onChange={(e: any) => {
                    const selected = aims.findIndex(aim => aim.name === e.target.value);
                    setSelectedAim(selected);
                    const foundRanges = getUniqueRanges(selected);
                    setRanges(foundRanges);
                  }}
                  variant-xl
                >
                  {aims.map(t => (
                    <MenuItem key={t.id} value={t.name} data-testid="sibling-menuitem">
                      <AcUnitIcon />{' '}
                      <div style={{ marginLeft: '5px', display: 'inline-block' }}>{t.name}</div>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <KPIToolBar>
              <KPIToolBarMenu>
                <KPIToolBarMenuItem data-testid="KPIToolBar-item">Overview</KPIToolBarMenuItem>
                <KPIToolBarMenuItem data-testid="KPIToolBar-item">KPIs</KPIToolBarMenuItem>
                <KPIToolBarMenuItem data-testid="KPIToolBar-item">Projects</KPIToolBarMenuItem>
              </KPIToolBarMenu>
              <Flex>
                <ToggleButtonGroup data-testid="range-button-group">
                  {(!gotRanges.data && !gotRanges.error) || gotRanges.isLoading
                    ? getLoadingIndicator()
                    : ranges.map((range, i) => (
                        <ToggleButton
                          data-testid="range-button"
                          inactive={ranges[i].disabled}
                          key={i}
                          onClick={() => {
                            ranges[i].disabled = !ranges[i].disabled;
                            setRanges(ranges.slice());
                          }}
                        >
                          <IndicatorCircle variant-circleLarge color={`#${range.color}`} />
                          <IndicatorTitle>{range.name || 'No range'}</IndicatorTitle>
                        </ToggleButton>
                      ))}
                </ToggleButtonGroup>
              </Flex>
              <Flex>
                <ToggleButtonGroup>
                  <IconButton
                    data-testid="list-view-button"
                    variant={viewType !== 'list' ? 'contained' : 'outlined'}
                    onClick={() => {
                      setViewType('list');
                    }}
                  >
                    <ViewList />
                  </IconButton>
                  <IconButton
                    data-testid="grid-view-button"
                    variant={viewType !== 'grid' ? 'contained' : 'outlined'}
                    onClick={() => {
                      setViewType('grid');
                    }}
                  >
                    <ViewComfy />
                  </IconButton>
                </ToggleButtonGroup>
              </Flex>
            </KPIToolBar>
            {(!data && !error) || isLoading ? (
              getLoadingIndicator()
            ) : viewType === 'grid' ? (
              <GridView />
            ) : (
              <TableView />
            )}
          </Content>
        </Paper>
      </Grid>
    </Grid>
  );

  return <>{getPage()}</>;
};

export default KPIAimView;
